import { useCallback, useEffect, useMemo, useState } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useToast } from "../components/ui/use-toast";
import { useTranslation } from "react-i18next";
import * as adminSettingsService from "../services/adminSettingsService";
import type { AdminAuditLog, CryptoWalletSettings } from "../types/api";

const emptyWallets: CryptoWalletSettings = {
  usdtTrc20: "",
  usdtErc20: "",
  btc: "",
  eth: "",
  bnb: "",
};

const AdminCryptoWalletsPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [wallets, setWallets] = useState<CryptoWalletSettings>(emptyWallets);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AdminAuditLog[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(true);
  const [adminFilter, setAdminFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchWallets = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminSettingsService.getCryptoWalletSettings();
      setWallets({
        usdtTrc20: data.usdtTrc20 ?? "",
        usdtErc20: data.usdtErc20 ?? "",
        btc: data.btc ?? "",
        eth: data.eth ?? "",
        bnb: data.bnb ?? "",
      });
    } catch (error) {
      toast({
        title: t("admin.cryptoWallets.errorTitle"),
        description: t("admin.cryptoWallets.errorLoadDescription"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoadingAudit(true);
      const adminUserId = adminFilter.trim()
        ? Number.parseInt(adminFilter.trim(), 10)
        : undefined;
      const logs = await adminSettingsService.listCryptoWalletAuditLogs({
        adminUserId: Number.isFinite(adminUserId) ? adminUserId : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      setAuditLogs(logs);
    } catch {
      setAuditLogs([]);
    } finally {
      setLoadingAudit(false);
    }
  }, [adminFilter, startDate, endDate]);
  const exportCsv = useCallback(() => {
    const headers = [
      "id",
      "adminUserId",
      "adminName",
      "adminEmail",
      "adminRole",
      "action",
      "target",
      "createdAt",
      "beforeState",
      "afterState",
    ];
    const escapeValue = (value: string | number | null | undefined) => {
      const raw = value ?? "";
      const text = typeof raw === "string" ? raw : String(raw);
      return `"${text.replace(/"/g, '""')}"`;
    };
    const rows = auditLogs.map((log) =>
      [
        log.id,
        log.adminUserId,
        log.adminName ?? "",
        log.adminEmail ?? "",
        log.adminRole ?? "",
        log.action,
        log.target ?? "",
        log.createdAt,
        log.beforeState ?? "",
        log.afterState ?? "",
      ]
        .map(escapeValue)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "crypto-wallet-audit.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  }, [auditLogs]);


  useEffect(() => {
    void fetchWallets();
    void fetchAuditLogs();
  }, [fetchWallets, fetchAuditLogs]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload: CryptoWalletSettings = {
        usdtTrc20: wallets.usdtTrc20?.trim() || null,
        usdtErc20: wallets.usdtErc20?.trim() || null,
        btc: wallets.btc?.trim() || null,
        eth: wallets.eth?.trim() || null,
        bnb: wallets.bnb?.trim() || null,
      };
      await adminSettingsService.updateCryptoWalletSettings(payload);
      await fetchAuditLogs();
      toast({
        title: t("admin.cryptoWallets.successTitle"),
        description: t("admin.cryptoWallets.successDescription"),
        variant: "success",
      });
    } catch (error) {
      toast({
        title: t("admin.cryptoWallets.errorTitle"),
        description: t("admin.cryptoWallets.errorSaveDescription"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold text-foreground dark:text-white">
          {t("admin.cryptoWallets.title")}
        </h2>
        <p className="text-sm text-muted-foreground dark:text-white/70">
          {t("admin.cryptoWallets.subtitle")}
        </p>
      </div>

      <div className="rounded-3xl border border-border bg-muted/50 p-6 text-foreground dark:border-white/10 dark:bg-black/30 dark:text-white">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs text-muted-foreground dark:text-white/60">
              {t("admin.cryptoWallets.usdtTrc20Label")}
            </label>
            <Input
              value={wallets.usdtTrc20 ?? ""}
              onChange={(event) =>
                setWallets((prev) => ({
                  ...prev,
                  usdtTrc20: event.target.value,
                }))
              }
              placeholder={t("admin.cryptoWallets.usdtTrc20Placeholder")}
              className="mt-2 bg-background border-border text-foreground dark:bg-white/5 dark:border-white/10 dark:text-white"
              disabled={loading}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground dark:text-white/60">
              {t("admin.cryptoWallets.usdtErc20Label")}
            </label>
            <Input
              value={wallets.usdtErc20 ?? ""}
              onChange={(event) =>
                setWallets((prev) => ({
                  ...prev,
                  usdtErc20: event.target.value,
                }))
              }
              placeholder={t("admin.cryptoWallets.usdtErc20Placeholder")}
              className="mt-2 bg-background border-border text-foreground dark:bg-white/5 dark:border-white/10 dark:text-white"
              disabled={loading}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground dark:text-white/60">
              {t("admin.cryptoWallets.btcLabel")}
            </label>
            <Input
              value={wallets.btc ?? ""}
              onChange={(event) =>
                setWallets((prev) => ({ ...prev, btc: event.target.value }))
              }
              placeholder={t("admin.cryptoWallets.btcPlaceholder")}
              className="mt-2 bg-background border-border text-foreground dark:bg-white/5 dark:border-white/10 dark:text-white"
              disabled={loading}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground dark:text-white/60">
              {t("admin.cryptoWallets.ethLabel")}
            </label>
            <Input
              value={wallets.eth ?? ""}
              onChange={(event) =>
                setWallets((prev) => ({ ...prev, eth: event.target.value }))
              }
              placeholder={t("admin.cryptoWallets.ethPlaceholder")}
              className="mt-2 bg-background border-border text-foreground dark:bg-white/5 dark:border-white/10 dark:text-white"
              disabled={loading}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground dark:text-white/60">
              {t("admin.cryptoWallets.bnbLabel")}
            </label>
            <Input
              value={wallets.bnb ?? ""}
              onChange={(event) =>
                setWallets((prev) => ({ ...prev, bnb: event.target.value }))
              }
              placeholder={t("admin.cryptoWallets.bnbPlaceholder")}
              className="mt-2 bg-background border-border text-foreground dark:bg-white/5 dark:border-white/10 dark:text-white"
              disabled={loading}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white hover:from-cyan-500 hover:to-blue-600">
            {saving
              ? t("admin.cryptoWallets.saving")
              : t("admin.cryptoWallets.saveButton")}
          </Button>
        </div>
      </div>

      <div className="rounded-3xl border border-border bg-muted/50 p-6 text-foreground dark:border-white/10 dark:bg-black/30 dark:text-white">
        <h3 className="text-lg font-semibold">
          {t("admin.cryptoWallets.auditTitle", "Historique")}
        </h3>
        <p className="text-xs text-muted-foreground dark:text-white/60">
          {t(
            "admin.cryptoWallets.auditSubtitle",
            "Dernières modifications enregistrées."
          )}
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-muted-foreground dark:text-white/60">
              {t("admin.cryptoWallets.auditFilterLabel", "ID admin")}
            </label>
            <Input
              value={adminFilter}
              onChange={(event) => setAdminFilter(event.target.value)}
              placeholder={t(
                "admin.cryptoWallets.auditFilterPlaceholder",
                "Ex: 12"
              )}
              className="mt-2 w-40 bg-background border-border text-foreground dark:bg-white/5 dark:border-white/10 dark:text-white"
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => fetchAuditLogs()}
            disabled={loadingAudit}
            className="h-9">
            {t("admin.cryptoWallets.auditFilterButton", "Filtrer")}
          </Button>
          <div>
            <label className="text-xs text-muted-foreground dark:text-white/60">
              {t("admin.cryptoWallets.auditStartDateLabel", "Début")}
            </label>
            <Input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              className="mt-2 w-40 bg-background border-border text-foreground dark:bg-white/5 dark:border-white/10 dark:text-white"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground dark:text-white/60">
              {t("admin.cryptoWallets.auditEndDateLabel", "Fin")}
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              className="mt-2 w-40 bg-background border-border text-foreground dark:bg-white/5 dark:border-white/10 dark:text-white"
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setAdminFilter("");
              setStartDate("");
              setEndDate("");
              void fetchAuditLogs();
            }}
            disabled={loadingAudit}
            className="h-9">
            {t("admin.cryptoWallets.auditClearButton", "Réinitialiser")}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={exportCsv}
            disabled={!auditLogs.length}
            className="h-9">
            {t("admin.cryptoWallets.auditExportCsv", "Exporter CSV")}
          </Button>
        </div>
        <div className="mt-4 space-y-3">
          {loadingAudit && (
            <p className="text-xs text-muted-foreground dark:text-white/60">
              {t("admin.cryptoWallets.auditLoading", "Chargement...")}
            </p>
          )}
          {!loadingAudit && auditLogs.length === 0 && (
            <p className="text-xs text-muted-foreground dark:text-white/60">
              {t("admin.cryptoWallets.auditEmpty", "Aucun historique.")}
            </p>
          )}
          {!loadingAudit &&
            auditLogs.map((log) => (
              <div
                key={log.id}
                className="rounded-2xl border border-border bg-card p-3 dark:border-white/10 dark:bg-white/5">
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground dark:text-white/60">
                  <span>ID #{log.id}</span>
                  <span>{new Date(log.createdAt).toLocaleString()}</span>
                </div>
                <div className="mt-2 text-xs text-muted-foreground dark:text-white/60">
                  {t("admin.cryptoWallets.auditAdminLabel", "Admin")}:{" "}
                  <span className="text-foreground/90 dark:text-white/80">
                    {log.adminName ||
                      log.adminEmail ||
                      `#${log.adminUserId}`}
                    {log.adminRole ? ` · ${log.adminRole}` : ""}
                  </span>
                </div>
                <div className="mt-2 grid gap-3 md:grid-cols-2">
                  <div>
                    <p className="text-[11px] uppercase text-muted-foreground dark:text-white/50">
                      {t("admin.cryptoWallets.auditBefore", "Avant")}
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      className="mt-2 h-7 px-2 text-[11px]"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            log.beforeState ?? "{}"
                          );
                          toast({
                            title: t(
                              "admin.cryptoWallets.copyJsonTitle",
                              "Copié"
                            ),
                            description: t(
                              "admin.cryptoWallets.copyJsonDescription",
                              "JSON copié."
                            ),
                          });
                        } catch {
                          toast({
                            title: t(
                              "admin.cryptoWallets.copyJsonErrorTitle",
                              "Erreur"
                            ),
                            description: t(
                              "admin.cryptoWallets.copyJsonErrorDescription",
                              "Impossible de copier."
                            ),
                            variant: "destructive",
                          });
                        }
                      }}>
                      {t("admin.cryptoWallets.copyJson", "Copier JSON")}
                    </Button>
                    <pre className="mt-1 max-h-32 overflow-auto rounded-lg bg-muted p-2 text-[11px] text-foreground dark:bg-black/40 dark:text-white/70">
                      {log.beforeState ?? "-"}
                    </pre>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase text-muted-foreground dark:text-white/50">
                      {t("admin.cryptoWallets.auditAfter", "Après")}
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      className="mt-2 h-7 px-2 text-[11px]"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            log.afterState ?? "{}"
                          );
                          toast({
                            title: t(
                              "admin.cryptoWallets.copyJsonTitle",
                              "Copié"
                            ),
                            description: t(
                              "admin.cryptoWallets.copyJsonDescription",
                              "JSON copié."
                            ),
                          });
                        } catch {
                          toast({
                            title: t(
                              "admin.cryptoWallets.copyJsonErrorTitle",
                              "Erreur"
                            ),
                            description: t(
                              "admin.cryptoWallets.copyJsonErrorDescription",
                              "Impossible de copier."
                            ),
                            variant: "destructive",
                          });
                        }
                      }}>
                      {t("admin.cryptoWallets.copyJson", "Copier JSON")}
                    </Button>
                    <pre className="mt-1 max-h-32 overflow-auto rounded-lg bg-muted p-2 text-[11px] text-foreground dark:bg-black/40 dark:text-white/70">
                      {log.afterState ?? "-"}
                    </pre>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default AdminCryptoWalletsPage;
